import { useRoute } from '#imports'

import type {
  BuybackOffer,
  Currency,
  InsuranceOffer,
} from '@backmarket/http-api'
import { getProductInfos } from '@backmarket/nuxt-layer-recommendation/helpers.ts'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { formatPrice } from '@backmarket/nuxt-module-tracking/formatPrice'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { isEmpty } from '@backmarket/utils/object/isEmpty'

import type { CartItem } from '@backmarket/nuxt-layer-checkout/api/cart/types'
import { DEFAULT_INSURANCE_ID } from '@backmarket/nuxt-layer-checkout/constants'

function getPaymentType(insurance?: InsuranceOffer) {
  if (!insurance || insurance.id.toString() === DEFAULT_INSURANCE_ID) return ''

  return insurance.isMonthly ? 'monthly' : 'upfront'
}

export const getTrackingProduct = (item: CartItem, currency: Currency) => {
  const insurance = item.insuranceOffers?.find(
    (offer) => offer.id.toString() !== DEFAULT_INSURANCE_ID && offer.selected,
  )

  const deliveryOption = item.options.find(
    (option) => option.type === 'delivery',
  )
  const deliveryOptionChoices = deliveryOption?.choices || []

  return {
    available: item.available === 'yes',
    brand: item.brand ?? '',
    category: item.category ?? '',
    color: item.color ?? '',
    currency,
    id: item.productId ?? '',
    insurancePrice: insurance?.price.amount ?? '',
    insuranceTitle: insurance?.title ?? '',
    insurancePaymentType: getPaymentType(insurance),
    list: getProductInfos(item.akeneoId).source,
    listingId: item.listingId ?? '',
    merchantId: item.merchantPublicId ?? item.merchantId ?? '',
    model: item.model ?? '',
    name: item.title ?? '',
    specialOfferType: item.specialOfferType,
    price: item.price ?? '',
    previousPrice: item.previousPrice?.amount ?? '',
    quantity: item.quantity ?? '',
    quantityMax: item.quantityMax ?? '',
    collectionPointAvailable: item.options.some((option) =>
      option.choices.some((choice) => choice.isCollectionPoint),
    ),
    shipper: [
      item.shippingSelected.shipperId ?? '',
      item.shippingSelected.shippingFeeDelay ?? '0',
      formatPrice({
        amount: item.shippingSelected.shippingFeePrice,
        currency,
      }),
    ].join(' - '),
    shippingDelay: item.shippingSelected.shippingFeeDelay ?? '',
    shippingPrice: item.shippingSelected.shippingFeePrice ?? '',
    shippingOptions: deliveryOptionChoices.map((choice) =>
      [
        choice.shipperId ?? '',
        choice.shippingDelay ?? '0',
        formatPrice({
          amount: choice.price,
          currency,
        }),
      ].join(' - '),
    ),
    uuid: item.akeneoId ?? '',
    variant: item.gradeId ?? '',
    warrantyDuration: item.warranties[0]?.delay ?? '',

    dealType: 'normal',
    isInsuranceEligible: !!item.insuranceOffers?.length || false,
    insuranceEligibleOffers:
      item.insuranceOffers &&
      item.insuranceOffers
        .filter((offer) => offer.id.toString() !== DEFAULT_INSURANCE_ID)
        .map((offer) => (offer.isMonthly ? 'monthly' : 'upfront'))
        .join('-'),
    mobilePlanOfferSelected: item.mobilePlan?.selectedOffer.name ?? '',
    mobilePlanOfferSelectedType: item.mobilePlan?.selectedOffer.type ?? '',
    isFlashSales: (item.promoCodes?.length ?? 0) > 0,
    campaignId: item.adjustmentFeeCampaignId ?? '',
    populationId: item.adjustmentFeePopulationId ?? '',
  }
}

const toViewCartItem = (item: CartItem, locationId: string, index: number) => {
  const previousPrice = item.previousPrice?.amount
    ? Number(item.previousPrice.amount)
    : item.price
  const discount = Math.max(previousPrice - item.price, 0)
  const shipping =
    'shippingFeeDelay' in item.shippingSelected ? item.shippingSelected : null

  /* eslint-disable camelcase */
  return {
    item_id: item.productId ?? '',
    item_name: item.title ?? '',
    affiliation: item.merchantPublicId ?? item.merchant ?? '',
    discount,
    index,
    item_brand: item.brand ?? '',
    price: item.price,
    quantity: item.quantity,
    item_category: item.category ?? '',
    item_variant: item.gradeName ?? '',
    item_warranty_duration: item.warranties[0]?.delay?.toString() ?? '',
    item_mobile_plan_name: item.mobilePlan?.selectedOffer.name ?? '',
    item_mobile_plan_carrier: item.mobilePlan?.selectedOffer.partner.name ?? '',
    location_id: locationId,
    google_business_vertical: 'retail',
    item_shipping_promise_text: shipping?.shippingDelayLabel ?? '',
    item_delivery_min_days: shipping?.shippingFeeDelay ?? 0,
    item_deal_type: item.specialOfferType?.toString() ?? '',
    item_units_in_stock: item.quantityMax,
    // TODO [BB-28.1]: item_average_rating, item_review_count, seller_average_rating, seller_review_count — not on CartItem (needs product/seller ratings API)
    // TODO [BB-28.2]: item_variant2 through item_variant15 — only grade variant is available on CartItem
    // TODO [BB-28.3]: item_category2 through item_category5 — only L1 category on CartItem
    // TODO [BB-28.4]: item_list_id, item_list_name — original add-to-cart source not persisted on CartItem
    // TODO [BB-28.5]: item_has_free_return, item_free_return_window, item_marketing_badge, item_is_favorited, item_finance_available, item_finance_provider, item_is_trade_in_eligible — not on CartItem
    // TODO [BB-28.6]: item_mobile_plan_data_allowance, item_mobile_plan_commitment_months, item_mobile_plan_monthly_price — not on MobilePlanOffer
  }
  /* eslint-enable camelcase */
}

/**
 * Maps a cart item for the `begin_checkout` event. Shares all `view_cart`
 * fields and adds the per-item insurance fields, which FIRST APPEAR in
 * `begin_checkout` (the insurance choice is confirmed when clicking "Continue").
 */
export const toBeginCheckoutItem = (
  item: CartItem,
  locationId: string,
  index: number,
) => {
  const insurance = item.insuranceOffers?.find(
    (offer) => offer.id.toString() !== DEFAULT_INSURANCE_ID && offer.selected,
  )

  let insuranceType = ''
  if (insurance) insuranceType = insurance.isMonthly ? 'monthly' : 'one_time'

  /* eslint-disable camelcase */
  return {
    ...toViewCartItem(item, locationId, index),
    item_insurance_name: insurance?.title ?? 'none',
    item_insurance_type: insuranceType,
    item_insurance_price: insurance ? Number(insurance.price.amount) : 0,
    item_insurance_currency: insurance?.price.currency ?? '',
    item_insurance_term_months: insurance?.totalCoverageDurationInMonths ?? 0,
    item_insurance_is_recommended: insurance?.isRecommended ?? false,
  }
  /* eslint-enable camelcase */
}

/**
 * Builds the event-level `trade_in_details` object shared by `view_cart` and
 * `begin_checkout`. Returns an empty object when there is no swap offer, so it
 * can be spread directly into the event payload.
 */
const buildTradeInDetails = (
  swapOffer: BuybackOffer | null,
  currency: Currency,
): Record<string, Record<string, unknown>> => {
  if (isEmpty(swapOffer) || !swapOffer) return {}

  /* eslint-disable camelcase */
  return {
    trade_in_details: {
      trade_in_id: swapOffer.sourcingListingPublicId ?? '',
      trade_in_item_category:
        (swapOffer.diagnosticPayload?.category as string) ?? '',
      trade_in_price: swapOffer.price,
      trade_in_currency: currency,
      // TODO [BB-28.7]: trade_in_item_brand, trade_in_item_name, trade_in_shipping_method, trade_in_payout_method, trade_in_payout_timing, trade_in_questions[] — not on BuybackOffer
    },
  }
  /* eslint-enable camelcase */
}

export function useCheckoutTracking() {
  const route = useRoute()
  const {
    trackFunnel: track,
    trackViewCart: trackViewCartEvent,
    trackBeginCheckout: trackBeginCheckoutEvent,
  } = useTracking()
  const { defaultCurrency, countryCode } = useMarketplace().market

  const trackFunnel = (
    items: Array<CartItem>,
    swapOffer: BuybackOffer | null,
  ) => {
    track({
      products: items.map((item) => getTrackingProduct(item, defaultCurrency)),
      swap: !isEmpty(swapOffer),
      swapCategory: !isEmpty(swapOffer)
        ? (swapOffer?.diagnosticPayload?.category as string)
        : null,
      swapEstimation: !isEmpty(swapOffer) ? swapOffer.price : null,
      step: route.meta?.gtmStepId ?? null,
      pageType: (route.name as string) ?? '',
    })
  }

  const cartValue = (items: Array<CartItem>) =>
    items.reduce((sum, item) => sum + item.price * item.quantity, 0)

  const trackViewCart = (
    items: Array<CartItem>,
    swapOffer: BuybackOffer | null,
  ) => {
    /* eslint-disable camelcase */
    trackViewCartEvent({
      has_trade_in: !isEmpty(swapOffer),
      ecommerce: {
        currency: defaultCurrency,
        value: cartValue(items),
        items: items.map((item, index) =>
          toViewCartItem(item, countryCode, index),
        ),
      },
      ...buildTradeInDetails(swapOffer, defaultCurrency),
    })
    /* eslint-enable camelcase */
  }

  const trackBeginCheckout = (
    items: Array<CartItem>,
    swapOffer: BuybackOffer | null,
  ) => {
    /* eslint-disable camelcase */
    trackBeginCheckoutEvent({
      has_trade_in: !isEmpty(swapOffer),
      ecommerce: {
        currency: defaultCurrency,
        value: cartValue(items),
        items: items.map((item, index) =>
          toBeginCheckoutItem(item, countryCode, index),
        ),
      },
      ...buildTradeInDetails(swapOffer, defaultCurrency),
    })
    /* eslint-enable camelcase */
  }

  return { trackFunnel, trackViewCart, trackBeginCheckout }
}
